import { useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileText, Loader2 } from "lucide-react";
import "@/styles/legal-prose.css";

const API = process.env.REACT_APP_BACKEND_URL;

// Renders any legal doc (SLA / DPA / MSA / Order Form) inline so prospects
// can read the entire agreement before downloading. Slug comes from URL:
//   /legal/sla        → SLA
//   /legal/dpa        → DPA
//   /legal/msa        → MSA
//   /legal/order-form → Order Form
export default function LegalRenderPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/legal/render/${slug}`);
        setDoc(data);
      } catch (e) {
        if (e?.response?.status === 404) setErr("This document doesn't exist.");
        else setErr("Could not load the document. Try again in a moment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-200" data-testid={`legal-render-${slug}`}>
      <div className="max-w-4xl mx-auto px-6 py-14">
        {/* Header nav */}
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="inline-flex items-center gap-3 text-white hover:opacity-80" data-testid="legal-render-home">
            <img src="/prestigious-logo.png" alt="Prestigious Property Management Services" className="h-12 w-12" />
          </Link>
          <button
            type="button"
            onClick={() => navigate("/legal/downloads")}
            className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
            data-testid="legal-render-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All documents
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading contract…
          </div>
        )}

        {err && (
          <div className="rounded-lg border border-red-500/40 bg-red-950/40 text-red-300 px-4 py-3">
            {err}
            <div className="mt-3">
              <Link to="/legal/downloads" className="text-orange-300 hover:text-orange-200 text-sm">
                ← Back to legal downloads
              </Link>
            </div>
          </div>
        )}

        {doc && (
          <>
            {/* Doc header */}
            <header className="mb-10 pb-8 border-b border-white/5">
              <div className="text-[11px] uppercase tracking-[0.28em] text-orange-400 mb-3">
                {doc.kicker}
              </div>
              <h1 className="text-4xl sm:text-5xl font-display font-semibold text-white leading-[1.05] tracking-tight">
                {doc.title}
              </h1>
              <p className="text-zinc-400 mt-4 max-w-2xl">{doc.description}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                <a
                  href={`${API}${doc.download_url}`}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-widest rounded-full px-4 py-2 border border-orange-500/50 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20 hover:text-white transition-colors"
                  data-testid={`legal-render-pdf-${slug}`}
                >
                  <Download className="h-3.5 w-3.5" /> Download PDF
                </a>
                <a
                  href={`${API}${doc.markdown_url}`}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-widest rounded-full px-4 py-2 border border-white/15 bg-white/[0.03] text-zinc-300 hover:bg-white/[0.08] hover:text-white transition-colors"
                  data-testid={`legal-render-md-${slug}`}
                >
                  <FileText className="h-3.5 w-3.5" /> Markdown source
                </a>
              </div>
              <div className="mt-4 text-xs text-zinc-500 tracking-wide uppercase">
                Version {doc.version} · February 2026
              </div>
            </header>

            {/* Rendered markdown body */}
            <article
              className="legal-prose"
              data-testid={`legal-render-body-${slug}`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.html || "") }}
            />

            {/* Footer nav */}
            <footer className="mt-16 pt-8 border-t border-white/5 text-xs text-zinc-500 flex flex-wrap gap-4">
              <Link to="/legal/sla" className="hover:text-white">SLA</Link>
              <Link to="/legal/dpa" className="hover:text-white">DPA</Link>
              <Link to="/legal/msa" className="hover:text-white">MSA</Link>
              <Link to="/legal/order-form" className="hover:text-white">Order Form</Link>
              <Link to="/legal/downloads" className="hover:text-white">Downloads</Link>
              <span className="ml-auto">© 2026 Prestigious HOA Portal</span>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
